package com.evoapps.evoshift.domain.model

/**
 * SH-259: a manager's notes on a shift: why nobody came ([Shift.noShowReason]) and why it was split
 * ([ShiftSplit.reason], e.g. "Jan fell ill"). Kept out of the shift document
 * (`shift_manager_notes/{shiftId}`): colleagues read shifts where the roster is open to the team
 * (SH-255), and only the workplace's managers and owner read these. The screens see them on the
 * shift as before ([applyTo]); the repositories write them to their own document.
 */
data class ShiftManagerNotes(
    val shiftId: String,
    val rosterId: String,
    val noShowReason: String? = null,
    val splitReason: String? = null
) {
    val isEmpty: Boolean get() = noShowReason == null && splitReason == null

    /**
     * [shift] with these notes, where they apply (a no-show's reason while nobody came, a split's
     * while it is split). A note missing here keeps what the shift document still carries: a shift
     * an older app wrote, not moved yet (functions/shiftManagerNotes.js moves those).
     */
    fun applyTo(shift: Shift): Shift = shift.copy(
        noShowReason = if (shift.notWorked) noShowReason ?: shift.noShowReason else shift.noShowReason,
        split = shift.split?.let { it.copy(reason = splitReason ?: it.reason) }
    )

    companion object {
        /** The notes [shift] carries (as the screens set them), for writing; null: none. */
        fun of(shift: Shift): ShiftManagerNotes? = ShiftManagerNotes(
            shiftId = shift.id,
            rosterId = shift.rosterId,
            noShowReason = shift.noShowReason?.takeIf { shift.notWorked },
            splitReason = shift.split?.reason
        ).takeUnless { it.isEmpty || shift.id.isEmpty() }
    }
}

/** SH-259: [shifts] with their notes ([ShiftManagerNotes.applyTo]); [notes] by shift id. */
fun withManagerNotes(shifts: List<Shift>, notes: Map<String, ShiftManagerNotes>): List<Shift> =
    if (notes.isEmpty()) shifts else shifts.map { shift -> notes[shift.id]?.applyTo(shift) ?: shift }
